import { useCallback, useEffect, useState } from "react";
import { Download, FileBadge, Send, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const openPacket = async (packetId, onError) => {
  try {
    const response = await api.get(`/compliance/clearance-packets/${packetId}/download-link`);
    window.open(`${process.env.REACT_APP_BACKEND_URL}${response.data.path}`, "_blank", "noopener");
  } catch (requestError) {
    onError(formatApiError(requestError));
  }
};

export const ClearancePacketActions = ({ assignment, onChange }) => {
  const [data, setData] = useState({ packets: [], events: [], eligibility: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await api.get("/compliance/clearance-packets", { params: { assignment_id: assignment.id } });
      setData(response.data);
      setError("");
      onChange?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [assignment.id, onChange]);

  useEffect(() => {
    load();
  }, [load]);

  async function generate() {
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/compliance/clearance-packets", { assignment_id: assignment.id });
      setNotice(`Clearance packet generated for ${response.data.provider_name} · ${response.data.client_name}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  async function sendToPortal(packetId) {
    setBusy(true);
    try {
      await api.post(`/compliance/clearance-packets/${packetId}/send-to-portal`);
      setNotice("Packet posted to the client's clearance summary page.");
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  const blocked = data.eligibility && !data.eligibility.eligible;
  return (
    <section className="assignment-status-panel clearance-packet-panel" data-testid="clearance-packet-panel">
      <div className="section-heading">
        <div>
          <h3><FileBadge size={17} /> Clearance packet · {assignment.client_name}</h3>
          <p>{assignment.status ? `${assignment.status} · starts ${assignment.start_date || "TBD"}. ` : ""}One-page PDF of this provider's clearance status for {assignment.client_name}: the client's active requirements, licenses, Medicaid and exclusion screening. No source documents or results are included.</p>
        </div>
        <button type="button" className="button button-primary" data-testid="generate-clearance-packet" disabled={busy || blocked || !data.eligibility} onClick={generate}>
          <FileBadge size={15} /> {busy ? "Working…" : "Generate clearance packet"}
        </button>
      </div>
      {blocked && <p className="form-message error-message clearance-blocked" data-testid="clearance-packet-blocked"><ShieldAlert size={14} /> {data.eligibility.reason}</p>}
      {error && <p className="form-message error-message" data-testid="clearance-packet-error">{error}</p>}
      {notice && <p className="form-message success-message" data-testid="clearance-packet-notice">{notice}</p>}
      {data.packets.length > 0 && (
        <ul className="clearance-packet-list" data-testid="clearance-packet-list">
          {data.packets.map((packet) => (
            <li key={packet.id} data-testid={`clearance-packet-${packet.id}`}>
              <div>
                <strong>{packet.filename}</strong>
                <small>Generated {new Date(packet.generated_at).toLocaleString()} by {packet.generated_by_name}{packet.portal_visible_at ? ` · Sent to client portal ${new Date(packet.portal_visible_at).toLocaleDateString()} by ${packet.portal_sent_by_name}` : ""}</small>
              </div>
              <div className="button-row">
                <button type="button" className="button button-secondary" data-testid={`download-packet-${packet.id}`} onClick={() => openPacket(packet.id, setError)}><Download size={14} /> Download</button>
                {!packet.portal_visible_at && <button type="button" className="button button-ghost" data-testid={`send-packet-${packet.id}`} disabled={busy} onClick={() => sendToPortal(packet.id)}><Send size={14} /> Send to client portal</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
